import { Check, X } from "lucide-react";

import type { AccessRole } from "@/modules/dashboard/settings/types/access.types";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

/** How many policy chips fit a table cell before the rest collapse into `+N`. */
const VISIBLE_POLICIES = 3;

export interface RolePoliciesCellProps {
  policies: AccessRole["policies"];
}

/**
 * The policies a role is built from, as chips coloured by effect.
 *
 * Zero policies is not an empty cell but a stated finding: assigning such a role grants nothing, which is
 * exactly the kind of thing an empty cell would let a reader skim past.
 */
export function RolePoliciesCell({ policies }: RolePoliciesCellProps) {
  const { t } = useTranslations("settings");

  if (policies.length === 0) {
    return <ColocaBadge variant="warning">{t("access_roles_no_policies_badge")}</ColocaBadge>;
  }

  const visible = policies.slice(0, VISIBLE_POLICIES);
  const extra = policies.length - visible.length;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map((policy) => (
        <span
          key={policy.id}
          className={cn(
            "inline-flex max-w-[150px] items-center gap-1 truncate rounded-sm border px-1.5 py-px font-mono text-[9.5px]",
            policy.effect === "deny"
              ? "border-destructive/40 bg-destructive/10 text-destructive"
              : "border-primary/30 bg-primary/10 text-primary",
          )}
          title={policy.name}
        >
          {policy.effect === "deny" ? (
            <X size={8} className="shrink-0" />
          ) : (
            <Check size={8} className="shrink-0" />
          )}
          <span className="truncate">{policy.name}</span>
        </span>
      ))}
      {extra > 0 && <ColocaBadge variant="secondary">+{extra}</ColocaBadge>}
    </div>
  );
}
